import { engineComponent, NumberUtils } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../Components/ComponentTypes";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Symbols } from "../GameComponents/Symbols";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { BaseView } from "../GameComponents/Base/BaseView";
import { FreespinsView } from "../GameComponents/Freespins/FreespinsView";
import { PlayList } from "../Components/Sounds/PlayList";
import { ModuleManager } from "../Managers/ModuleManager";
import { Payline } from "../GameComponents/Payline";
import { Symbol } from "../GameComponents/Symbol";
import { PaylinesExtended } from "./PaylinesExtended";
import { Settings } from "./Settings";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { BetManager } from "../Managers/BetManager";
import { ReelExtended } from "./ReelExtended";
import { SymbolExtended } from "./SymbolExtended";
import { PaylinesDefinitions } from "../GameComponents/PaylinesDefinitions";
import { RopeElement } from "./RopeElement";
import { CustomUtils } from "../Utils/CustomUtils";
import { AppContext } from "../../Common/AppContext";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { Languages } from "../Utils/Languages";
import { GroupTweens } from "./GroupTweens";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    public groupTweens: SwitchElement;

    // ActiveElements
    private _activeElementsNames: Array<string> = [];
    private _activeElements: Array<SwitchElement> = [];
    private _activeElementsCase: string;

    private _finishFirstWinsDelay: FrameTimerEventEmitter;
    private _teasingPulseSoundTimer: FrameTimerEventEmitter;
    private _allWinSymbolsMask: Array<Array<boolean>>;

    protected _scattertCount: number = 0;
    protected isFsWin: boolean = false;
    protected freespinsWin: Communication.Game.IWin = null;
    protected paylinesLines: PaylinesExtended;
    protected noActiveWinContainer: ContainerElement;
    protected noTrimWinContainer: ContainerElement;

    private static readonly IdleStep: string = "idleStep";
    private currentIdleSteps: Array<Array<Array<number>>>;

    configure(node: Element): void {
        super.configure(node);

        if (node.hasAttribute(WinAnimatorExtendedAttributes.ACTIVE_ELEMENTS)) {
            this._activeElementsNames = node.getAttribute(WinAnimatorExtendedAttributes.ACTIVE_ELEMENTS).split(";");
        }
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected createSymbols(
        symbols: Symbols,
        noWinSymbolsContainer: ContainerElement,
        symbolsContainer: ContainerElement,
    ): void {
        super.createSymbols(symbols, noWinSymbolsContainer, symbolsContainer);

        this._allWinSymbolsMask = [];

        for (let i = 0; i < this.reelCount; i++) {
            this._allWinSymbolsMask.push([]);

            for (let j = 0; j < this.symbolCount; j++) {
                this._allWinSymbolsMask[i].push(false);
            }
        }
    }

    protected createPaylines(paylines: PaylinesExtended, paylinesLines?: PaylinesExtended): void {
        if (paylines) {
            this.paylines = paylines;
        }

        if (paylinesLines) {
            this.paylinesLines = paylinesLines;
        }

        // this.allPaylinesGoToCase(Payline.CaseNormal);
        this.allPaylinesGoToCase(Payline.CaseInit);
    }

    initialize(
        symbols: Symbols,
        paylines?: PaylinesExtended,
        paylinesLines?: PaylinesExtended,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
        parent?: BaseView | FreespinsView,
    ): void {
        super.initialize(symbols, paylines, noWinSymbolsContainer, symbolsContainer);

        this._finishFirstWinsDelay = CreateFrameTimer("finishFirstWinsDelay");
        this._finishFirstWinsDelay.on(FrameTimerEvents.Complete, this.finishFirstWins.bind(this));

        this._teasingPulseSoundTimer = CreateFrameTimer("teasingPulseSoundTimer");
        this._teasingPulseSoundTimer.on(FrameTimerEvents.Complete, function () {
            PlayList.play("SND_BaseScatterPulse3");
        });

        if (paylinesLines) {
            this.createPaylines(null, paylinesLines);
        }

        if (parent) {
            this._activeElementsNames.forEach((elementName) => {
                if (parent.childs[elementName] && parent.childs[elementName] instanceof SwitchElement) {
                    this._activeElements.push(parent.childs[elementName]);
                }
            });

            if (parent.childs["NoActiveWinContainer"]) {
                this.noActiveWinContainer = parent.childs["NoActiveWinContainer"];
            }

            if (parent.childs["NoTrimWinContainer"]) {
                this.noTrimWinContainer = parent.childs["NoTrimWinContainer"];
            }

            if (parent.childs["GroupTweens"]) {
                this.groupTweens = parent.childs["GroupTweens"];
            }
        }
    }

    protected allPaylinesGoToCase(caseType: string): void {
        if (this.paylines != null) {
            this.paylines.goToCase(caseType);
        }

        if (this.paylinesLines != null) {
            this.paylinesLines.goToCase(caseType);
        }
    }

    protected winPaylineGoToCase(winIndex: number, caseType: string): void {
        if (this.paylines != null) {
            const win = this.currentWins[winIndex];

            if (win.trigger === Communication.Game.WinTrigger.Payline) {
                if (caseType === Payline.CaseCycle && this.currentWins.length === 1) {
                    this.paylines.goToCase(Payline.CaseSingleCycle, win.paylineId);

                    if (this.paylinesLines != null) {
                        this.paylinesLines.goToCase(Payline.CaseSingleCycle, win.paylineId);
                    }
                } else {
                    this.paylines.goToCase(caseType, win.paylineId);

                    if (this.paylinesLines != null) {
                        this.paylinesLines.goToCase(caseType, win.paylineId);

                        // if Payline named "PaylineLine_1" ....
                        this.updateRopeElementPointsInPayline(win, win.paylineId);
                    }
                }
            }
        }
    }

    public onOneReelRollFinished(reelIndex: number): void {
        // NO WIN symbols when is Teasing (onOneReelRollFinished is before onReelStopped)
        // this is only for reels that have already been called onReelStopped (this.currentSymbols[x][y] != null)
        if (ReelExtended.Tease && reelIndex && reelIndex >= 0) {
            for (let i = 0; i <= reelIndex; i++) {
                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    const symbol = this.currentSymbols[i][j];
                    if (symbol !== null && symbol.id + 1 != Settings.SymbolTeasing) {
                        symbol.goToCase(Symbol.CaseNowin);
                    }
                }
            }
        }
    }

    public onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        this.currentReels = reelsData;

        for (let i = 0; i < reelsData[reelIndex].symbols.length; i++) {
            this.removeCurrentSymbol(reelIndex, i);
        }

        for (let i = 0; i < reelsData[reelIndex].symbols.length; i++) {
            // for (let i = reelsData[reelIndex].symbols.length - 1; i >= 0; i--) {
            this.addCurrentSymbol(reelIndex, i);
        }

        for (let j = 0; j < this.currentSymbols[reelIndex].length; j++) {
            const symbol = this.currentSymbols[reelIndex][j];
            if (symbol !== null) {
                // if (reelIndex === 0 || (reelIndex === 2 && this._scattertCount === 1) || (reelIndex === 4 && this._scattertCount === 2)) {
                if (symbol.id + 1 == Settings.SymbolBonus) {
                    // symbol.goToCase(Symbol.CaseImpact);
                    this._scattertCount++;
                    //PlayList.play('SND_BaseBonusHit0' + this._scattertCount);
                }
                // }
            }
        }

        this.sortSymbols();

        if (ReelExtended.Tease) {
            if (reelIndex == this.reelCount - 1) {
                // stop teasing
                if (this._scattertCount < 3) {
                    // nowin after machine stop
                    for (let i = 0; i < this.currentSymbols.length - 1; i++) {
                        for (let j = 0; j < this.currentSymbols[i].length; j++) {
                            const symbol = this.currentSymbols[i][j];
                            if (symbol !== null) {
                                if (symbol.id + 1 != Settings.SymbolTeasing) {
                                    symbol.goToCase(Symbol.CaseNormal);
                                } else {
                                    if (symbol.getCurrentCase() !== Symbol.CaseImpact) {
                                        symbol.goToCase(Symbol.CaseNormal);
                                    }
                                }
                            }
                        }
                    }
                } else {
                    // win after machine stop
                    for (let j = 0; j < this.currentSymbols[this.reelCount - 1].length; j++) {
                        const symbol = this.currentSymbols[this.reelCount - 1][j];
                        if (symbol !== null) {
                            if (symbol.id + 1 != Settings.SymbolTeasing) {
                                symbol.goToCase(Symbol.CaseNowin);
                            } else {
                                // if (symbol.getCurrentCase() !== Symbol.CaseImpact) {
                                //     symbol.goToCase(Symbol.CaseTease);
                                //     this.symbolsContainer.addChild(symbol);
                                // }
                            }
                        }
                    }
                }
            } else {
                // start teasing
                let bullSound: boolean = false;
                for (let i = 0; i < this.currentSymbols.length - 1; i++) {
                    for (let j = 0; j < this.currentSymbols[i].length; j++) {
                        const symbol = this.currentSymbols[i][j];
                        if (symbol !== null) {
                            if (symbol.id + 1 != Settings.SymbolTeasing) {
                                symbol.goToCase(Symbol.CaseNowin);
                            } else {
                                if (this._scattertCount >= 2) {
                                    symbol.goToCase(SymbolExtended.CaseTease);

                                    if (!bullSound) {
                                        bullSound = true;
                                        this._teasingPulseSoundTimer.stop();
                                        this._teasingPulseSoundTimer.start(20);
                                    }

                                    // this.symbolsContainer.addChild(symbol);
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    public showSymbols(reelsData: Array<Communication.Game.IReel>): void {
        this.currentReels = reelsData;

        for (let i = 0; i < reelsData.length; i++) {
            for (let j = 0; j < reelsData[i].symbols.length; j++) {
                this.removeCurrentSymbol(i, j);
            }
        }

        for (let i = 0; i < reelsData.length; i++) {
            // for (let j = reelsData[i].symbols.length - 1; j >= 0; j--) {
            for (let j = 0; j < reelsData[i].symbols.length; j++) {
                this.addCurrentSymbol(i, j);
            }
        }

        this.sortSymbols();
    }

    protected addCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        if (this.currentSymbols[reelIndex][symbolIndex] === null) {
            const id = this.currentReels[reelIndex].symbols[symbolIndex];

            const symbol = this.symbols.getSymbol(id - 1, Symbol.CaseNormal);
            symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
            symbol.position.y = this.reelY[reelIndex] + this.symbolHeight * symbolIndex - symbol.offsetY;

            this.symbolsContainer.addChild(symbol);
            // this.symbolsContainer.addChildAt(symbol, 0);

            this.currentSymbols[reelIndex][symbolIndex] = symbol;
        }
    }

    protected allSymbolsGoToCase(caseType: string, noWin: boolean): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                // for (let j = this.currentSymbols[i].length - 1; j >= 0; j--) {
                const symbol = this.currentSymbols[i][j];

                if (symbol !== null && caseType == "") {
                    if (noWin) {
                        this.noWinSymbolsContainer.addChild(symbol);
                        // this.noWinSymbolsContainer.addChildAt(symbol, 0);
                    } else {
                        this.symbolsContainer.addChild(symbol);
                        // this.symbolsContainer.addChildAt(symbol, 0);
                    }

                    continue;
                }

                if (symbol !== null && this.allowSymbolGoToCase(i, j, caseType)) {
                    symbol.goToCase(caseType);

                    if (noWin) {
                        this.noWinSymbolsContainer.addChild(symbol);
                        // this.noWinSymbolsContainer.addChildAt(symbol, 0);
                    } else {
                        this.symbolsContainer.addChild(symbol);
                        // this.symbolsContainer.addChildAt(symbol, 0);
                    }
                }
            }
        }

        this.sortSymbols();
    }

    public sortSymbols(): void {
        if (!this.symbols.hasZindex) {
            return;
        }
        let addQueue: Symbol[] = [];
        this.symbolsContainer.children.forEach((symbol) => {
            addQueue.push(<Symbol>symbol);
        });
        addQueue.sort((a, b) => a.zIndex - b.zIndex);
        addQueue.forEach((symbol) => {
            this.symbolsContainer.addChild(symbol);
        });
        addQueue = [];
        this.noWinSymbolsContainer.children.forEach((symbol) => {
            addQueue.push(<Symbol>symbol);
        });
        addQueue.sort((a, b) => a.zIndex - b.zIndex);
        addQueue.forEach((symbol) => {
            this.noWinSymbolsContainer.addChild(symbol);
        });
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        this.isFsWin = false;
        this.freespinsWin = null;

        if (wins != null && wins.length > 1) {
            for (let i = 0; i < wins.length; i++) {
                if (wins[i].type == Communication.Game.WinType.freeSpins) {
                    if (wins[i].trigger == Communication.Game.WinTrigger.Scatter) {
                        this.isFsWin = true;
                        this.freespinsWin = wins[i];
                    }
                }
            }
        }

        // ---- SHOW WINS ----
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.currentMultiplierIndex = 0;

            for (let i = 0; i < this.multipliers.length; i++) {
                if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                    this.currentMultiplierIndex = i;
                }
            }

            if (!this.showCustomWin()) {
                this.showFirstWins();
            }

            return this.currentMultiplierIndex >= 3;
        } else {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());

            return false;
        }
    }

    protected showFirstWins(): void {
        if (ModuleManager.freespinsActive() && this.groupTweens) {
            this.groupTweens.goToCase(GroupTweens.CaseWin);
        }

        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        // this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
            // this.winPaylineGoToCase(i, Payline.CaseWin);
        }

        // sort win symbols in win container after all paylines win -> winSymbolsGoToCase
        this.sortAllWinSymbolsByWinMask(this.symbolsContainer);

        this.startAnimation();
    }

    protected showFirstInitWins(): void {
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        // this.allPaylinesGoToCase(Payline.CaseNowin);

        // for (let i = 0; i < this.currentWins.length; i++) {
        //     this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
        //     this.winPaylineGoToCase(i, Payline.CaseWin);
        // }

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
        }
    }

    protected winSymbolsGoToCaseAsScatter(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                    const symbol = this.currentSymbols[i][j];
                    symbol.goToCase(caseType);

                    if (this.noTrimWinContainer) {
                        this.noTrimWinContainer.addChild(symbol);
                    } else {
                        this.symbolsContainer.addChild(symbol);
                    }

                    symbols.push(symbol);
                }
            }
        }

        if (symbols && symbols.length > 0) {
            PlayList.play("SND_BaseScatterPulse2");
        }

        return symbols;
    }

    protected winSymbolsGoToCaseAsPayline(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        if (this.paylines != null) {
            const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                win.paylineId,
                this.paylines.paylineDefinition,
            );

            for (let i = 0; i < definition.length; i++) {
                if (win.mask[i] === 1 && this.allowSymbolGoToCase(i, definition[i], caseType)) {
                    // save all symbols win mask
                    if (
                        this._allWinSymbolsMask != null &&
                        this._allWinSymbolsMask[i] != null &&
                        this._allWinSymbolsMask[i][definition[i]] != null &&
                        this._allWinSymbolsMask[i][definition[i]] == false
                    ) {
                        this._allWinSymbolsMask[i][definition[i]] = true;
                    }

                    const symbol = this.currentSymbols[i][definition[i]];
                    symbol.goToCase(caseType);

                    if (this.noActiveWinContainer != null && caseType == Symbol.CaseNormal) {
                        this.noActiveWinContainer.addChild(symbol);
                    } else {
                        this.symbolsContainer.addChild(symbol);
                    }

                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    protected finishFirstWins(): void {
        if (this.isFsWin) {
            if (this.showFsWin()) {
                return;
            }
        }

        this.emit(WinAnimator.FinishWinAnimator);

        this.currentCycle = 0;

        this.cycleWins(true);
    }

    protected showFsWin(): boolean {
        if (this.isFsWin && this.freespinsWin != null) {
            PlayList.play("SND_BaseScatterPulse2");

            this.isFsWin = false;
            this.currentWins = []; // clean all wins
            this.currentWins.push(this.freespinsWin); // add freespins win

            this.allSymbolsGoToCase(Symbol.CaseNowin, true);
            this.winSymbolsGoToCase(this.currentWins.length - 1, Symbol.CaseWin, false); // fs win on last index

            this._finishFirstWinsDelay.stop();
            this._finishFirstWinsDelay.start(80); // delay of animations scatter

            return true;
        }

        return false;
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    public reset(showLastSymbols: boolean, spin = false): boolean {
        this.stopTimer();

        PlayList.stop("SND_BaseWinline");

        // reset all wins symbols mask
        this.clearAllWinSymbolsMask();

        this._scattertCount = 0;

        this.allSymbolsGoToCase(showLastSymbols ? Symbol.CaseNormal : Symbol.CaseEmpty, false);

        this.allPaylinesGoToCase(Payline.CaseNormal);

        if (showLastSymbols && !spin) {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
        }

        this.value.visible = false;

        let lastMultiplierIndex = this.currentMultiplierIndex;
        this.currentMultiplierIndex = -1;

        if (this.currentAnimationIndex != null) {
            if (this.currentAnimationFinished) {
                this.resetAnimations();

                this.currentWins = null;
                this.currentWin = null;
                return false;
            } else {
                this.stopAnimation();

                this.currentWins = null;
                this.currentWin = null;
                return lastMultiplierIndex >= 2; // condition should be greater than or equals 2 beacause we`ve got only two types of base wins (before high wins)
            }
        } else {
            this.currentWins = null;
            this.currentWin = null;
            //return (lastMultiplierIndex >= 2); // condition should be greater than or equals 2 beacause we`ve got only two types of base wins (before high wins)
            return false;
        }
    }

    //******************************************************************************************************************
    //  ACTIVE ELEMENTS
    //******************************************************************************************************************

    /**
     * Attempts to switch all Active Elements to the earliest case from array the element can be switched to.
     * Elements with no case from the list are ignored.
     * @param s Array of names of cases to try to switch to
     * @param emitEvent
     * @param delay Switch delay
     */
    private setActiveElementCaseArray(s: Array<string>, emitEvent: boolean = false, delay?: number) {
        if (!s.length) {
            return;
        }
        if (!this.visible) {
            return;
        }
        this._activeElementsCase = s[0];
        this._activeElements.forEach((element) => {
            for (let i = 0; i < s.length; i++) {
                if (element.hasCase(s[i])) {
                    element.goToCase(s[i], emitEvent, delay);
                    break;
                }
            }
        });
    }

    /**
     * Attempts to switch all Active Elements to the given case if they have this case.
     * Elements without the case are ignored.
     * @param s Case name
     * @param emitEvent
     * @param delay
     */
    private setActiveElementCase(s: string, emitEvent: boolean = false, delay?: number) {
        if (!this.visible) {
            return;
        }
        this._activeElementsCase = s;
        this._activeElements.forEach((element) => {
            if (element.hasCase(s)) {
                element.goToCase(s, emitEvent, delay);
            }
        });
    }

    /**
     * Returns last case switched to, or the first case from a list switched to.
     */
    private getActiveElementCase(): string {
        return this._activeElementsCase;
    }

    //******************************************************************************************************************
    //  CYCLE WINS
    //******************************************************************************************************************

    protected cycleWins(isFirst: boolean = false): void {
        if (this.currentWins == null || ModuleManager.freespinsEnabled() || CustomUtils.isRetrigger()) {
            return;
        }

        if (isFirst) {
            this.cycleStep = 0;
        } else {
            this.cycleStep++;
        }

        PlayList.play("SND_BaseWinline");

        if (!ModuleManager.freespinsEnabled() && !ModuleManager.freespinsActive() && this.cycleStep % 4 === 0) {
            this.cycleStep = 0;
            // PlayList.stop('SND_BaseRecapLoop');
            PlayList.loop("SND_BaseRecapLoop");
        }

        this.allPaylinesGoToCase(Payline.CaseNowin);
        this.allSymbolsGoToCase("", true); // if case is empty, only move to container and no set new case (skip case)

        // if(this.currentCycle === 0) {
        //     for (let i = 0; i < this.currentWins.length; i++) {
        //         this.winPaylineGoToCase(i, Payline.CaseNormal);
        //         this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
        //     }
        // } else {
        for (let i = 0; i < this.currentWins.length; i++) {
            this.winPaylineGoToCase(i, Payline.CaseNormal);
            this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
        }
        // }

        this.sortAllWinSymbolsByWinMask(this.noActiveWinContainer); // sort noActiveWinContainer after call winSymbolsGoToCase (no actual cycle payline)

        if (this.currentWins[this.currentCycle].type == Communication.Game.WinType.freeSpins) {
            this.currentCycle++;
        }

        if (this.currentCycle < this.currentWins.length) {
            this.winSymbolsGoToCase(this.currentCycle, Symbol.CaseCycle, true);
            this.winPaylineGoToCase(this.currentCycle, Payline.CaseCycle);
        }

        this.currentCycle = (this.currentCycle + 1) % this.currentWins.length;

        this.startTimer(WinAnimator.CycleWins, this.getCycleWinsDelayWithCorrection(isFirst));
    }

    protected getCycleWinsDelay(): number {
        return 70;
    }

    private updateRopeElementPointsInPayline(wins: Object, paylineID: number): void {
        if (this.paylinesLines != null) {
            // payline element by ID (SwitchElement)
            let p: Payline = this.paylinesLines.childs["PaylineLine_" + paylineID];

            if (p && p instanceof Payline) {
                // array childs of Payline
                let o: Object = p.getChildElements() as Object;

                if (o && o instanceof Object) {
                    /*
                    // Color for TINT by win symbol
                    let winSymbol = 0;
                    let colorTint = 0xffffff;

                    if(wins) {
                        let symbols = wins['symbols'];
                        let mask = wins['mask'];

                        if (symbols && mask) {
                            for (let i = 0; i < symbols.length; i++) {
                                if(mask[i] === 1) {
                                    if(symbols[i] != 7 && symbols[i] != 8) {
                                        winSymbol = symbols[i];
                                    }
                                }
                            }
                        }
                    }

                    // color of symbol
                    switch(winSymbol) {
                        case 0:
                            colorTint = 0xffffff;
                            break;
                        case 1:
                            colorTint = 0xbef04b;
                            break;
                        case 2:
                            colorTint = 0x43e4f5;
                            break;
                        case 3:
                            colorTint = 0xf48225;
                            break;
                        case 4:
                            colorTint = 0xd176f7;
                            break;
                        case 5:
                            colorTint = 0xff054b;
                            break;
                        case 6:
                            colorTint = 0xfffe38;
                            break;
                        default:
                            colorTint = 0xffffff;
                            break;
                    }
                    */

                    // RopeElement
                    let ropeArray: Array<RopeElement> = [];

                    // if(o["RopeElementLine_tint"]) {
                    //     ropeArray.push(o["RopeElementLine_tint"]);
                    // }

                    if (o["RopeElementLine"]) {
                        ropeArray.push(o["RopeElementLine"]);
                    }

                    if (ropeArray && ropeArray.length > 0) {
                        let points: Array<PIXI.Point> = [];
                        const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                            paylineID,
                            this.paylines.paylineDefinition,
                        );

                        for (let i = 0; i < definition.length; i++) {
                            let w = this.symbolWidth;
                            let h = this.symbolHeight;
                            let x = this.reelX[i];
                            let y = this.reelY[i] + h * definition[i];

                            // ---------------------------------------------
                            // WARNING: symbol has pivot on left top corner
                            // ---------------------------------------------

                            // ADD FIRST POINT ON PAYLINE
                            if (i == 0) {
                                /* first point of payline, position on symbol "X"
                                       width
                                    /  0 0 0  \
                            height  |  X 0 0   | SYMBOL POSITION POINTS
                                    \  0 0 0  /
                            */
                                points.push(new PIXI.Point(x, y + h / 2));
                            }

                            // ADD other points
                            /* other point of payline, position on symbol "X"
                                       width
                                    /  0 0 0  \
                            height  |  0 X 0   | SYMBOL POSITION POINTS
                                    \  0 0 0  /
                            */
                            points.push(new PIXI.Point(x + w / 2, y + h / 2));

                            // ADD LAST POINT ON PAYLINE
                            if (i == definition.length - 1) {
                                /* last point of payline, position on symbol "X"
                                           width
                                        /  0 0 0  \
                                height  |  X 0 0   | SYMBOL POSITION POINTS
                                        \  0 0 0  /
                                */
                                // points.push(new PIXI.Point((x + w + w / 2), (y + h / 2)));
                                points.push(new PIXI.Point(x + w, y + h / 2)); // + offset X
                            }
                        }

                        for (let r = 0; r < ropeArray.length; r++) {
                            if (ropeArray[r] && ropeArray[r] instanceof RopeElement) {
                                // if(r === 0) {
                                //     ropeArray[r].setTint(colorTint);
                                // }

                                ropeArray[r].updateByArrayPIXIPoint(points);
                            }
                        }
                    }
                }
            }
        }
    }

    private sortAllWinSymbolsByWinMask(con: ContainerElement): void {
        if (con != null) {
            if (this._allWinSymbolsMask != null && this._allWinSymbolsMask.length > 0) {
                for (let i = 0; i < this._allWinSymbolsMask.length; i++) {
                    if (this._allWinSymbolsMask[i] != null) {
                        // for (let j = this._allWinSymbolsMask[i].length - 1; j >= 0; j--) {
                        for (let j = 0; j < this._allWinSymbolsMask[i].length; j++) {
                            if (this._allWinSymbolsMask[i][j] == true) {
                                const symbol = this.currentSymbols[i][j];

                                if (symbol) {
                                    con.addChild(symbol); // sort only wins symbols by win mask
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    private clearAllWinSymbolsMask(): void {
        if (this._allWinSymbolsMask != null && this._allWinSymbolsMask.length > 0) {
            for (let i = 0; i < this._allWinSymbolsMask.length; i++) {
                if (this._allWinSymbolsMask[i] != null) {
                    for (let j = this._allWinSymbolsMask[i].length - 1; j >= 0; j--) {
                        this._allWinSymbolsMask[i][j] = false;
                    }
                }
            }
        }
    }

    protected onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimator.Idle:
                this.idle();
                break;
            case WinAnimator.CycleWins:
                this.cycleWins();
                break;
            case WinAnimatorExtended.IdleStep:
                this.idleStep();
                break;
        }
    }

    //******************************************************************************************************************
    //  IDLE
    //******************************************************************************************************************

    protected idle(): void {
        this.currentIdleSteps = this.getIdleSteps();

        this.idleStep();
    }

    protected getInitialIdleDelay(): number {
        return 120;
    }

    protected getIdleDelay(): number {
        return 60 * (Math.floor(Math.random() * 3) + 2);
    }

    private idleStep(): void {
        const positions = this.currentIdleSteps.shift();

        for (let i = 0; i < positions.length; i++) {
            if (this.currentSymbols[positions[i][0]][positions[i][1]] != null) {
                this.currentSymbols[positions[i][0]][positions[i][1]].goToCase(Symbol.CaseIdle);
            }
        }

        if (this.currentIdleSteps.length === 0) {
            this.startTimer(WinAnimator.Idle, 90 + this.getIdleDelay());
        } else {
            this.startTimer(WinAnimatorExtended.IdleStep, 15);
        }
    }

    private getIdleSteps(): Array<Array<Array<number>>> {
        switch (Math.round(Math.random() * 9)) {
            default:
            case 0: // LEFT TOP
                return [
                    [[0, 0]],
                    [
                        [0, 1],
                        [1, 0],
                    ],
                    [
                        [0, 2],
                        [1, 1],
                        [2, 0],
                    ],
                    [
                        [0, 3],
                        [1, 2],
                        [2, 1],
                        [3, 0],
                    ],
                    [
                        [1, 3],
                        [2, 2],
                        [3, 1],
                        [4, 0],
                    ],
                    [
                        [2, 3],
                        [3, 2],
                        [4, 1],
                    ],
                    [
                        [3, 3],
                        [4, 2],
                    ],
                    [[4, 3]],
                ];
            case 1: // RIGHT TOP
                return [
                    [[4, 0]],
                    [
                        [4, 1],
                        [3, 0],
                    ],
                    [
                        [4, 2],
                        [3, 1],
                        [2, 0],
                    ],
                    [
                        [4, 3],
                        [3, 2],
                        [2, 1],
                        [1, 0],
                    ],
                    [
                        [3, 3],
                        [2, 2],
                        [1, 1],
                        [0, 0],
                    ],
                    [
                        [2, 3],
                        [1, 2],
                        [0, 1],
                    ],
                    [
                        [1, 3],
                        [0, 2],
                    ],
                    [[0, 3]],
                ];
            case 2: // LEFT BOTTOM
                return [
                    [[0, 3]],
                    [
                        [0, 2],
                        [1, 3],
                    ],
                    [
                        [0, 1],
                        [1, 2],
                        [2, 3],
                    ],
                    [
                        [0, 0],
                        [1, 1],
                        [2, 2],
                        [3, 3],
                    ],
                    [
                        [1, 0],
                        [2, 1],
                        [3, 2],
                        [4, 3],
                    ],
                    [
                        [2, 0],
                        [3, 1],
                        [4, 2],
                    ],
                    [
                        [3, 0],
                        [4, 1],
                    ],
                    [[4, 0]],
                ];
            case 3: // RIGHT BOTTOM
                return [
                    [[4, 3]],
                    [
                        [4, 2],
                        [3, 3],
                    ],
                    [
                        [4, 1],
                        [3, 2],
                        [2, 3],
                    ],
                    [
                        [4, 0],
                        [3, 1],
                        [2, 2],
                        [1, 3],
                    ],
                    [
                        [3, 0],
                        [2, 1],
                        [1, 2],
                        [0, 3],
                    ],
                    [
                        [2, 0],
                        [1, 1],
                        [0, 2],
                    ],
                    [
                        [1, 0],
                        [0, 1],
                    ],
                    [[0, 0]],
                ];
            case 4: // LEFT TO RIGHT
                return [
                    [
                        [0, 0],
                        [0, 1],
                        [0, 2],
                        [0, 3],
                    ],
                    [
                        [1, 0],
                        [1, 1],
                        [1, 2],
                        [1, 3],
                    ],
                    [
                        [2, 0],
                        [2, 1],
                        [2, 2],
                        [2, 3],
                    ],
                    [
                        [3, 0],
                        [3, 1],
                        [3, 2],
                        [3, 3],
                    ],
                    [
                        [4, 0],
                        [4, 1],
                        [4, 2],
                        [4, 3],
                    ],
                ];
            case 5: //  RIGHT TO LEFT
                return [
                    [
                        [4, 0],
                        [4, 1],
                        [4, 2],
                        [4, 3],
                    ],
                    [
                        [3, 0],
                        [3, 1],
                        [3, 2],
                        [3, 3],
                    ],
                    [
                        [2, 0],
                        [2, 1],
                        [2, 2],
                        [2, 3],
                    ],
                    [
                        [1, 0],
                        [1, 1],
                        [1, 2],
                        [1, 3],
                    ],
                    [
                        [0, 0],
                        [0, 1],
                        [0, 2],
                        [0, 3],
                    ],
                ];
            case 6: //  TOP TO BOTTOM
                return [
                    [
                        [0, 0],
                        [1, 0],
                        [2, 0],
                        [3, 0],
                        [4, 0],
                    ],
                    [
                        [0, 1],
                        [1, 1],
                        [2, 1],
                        [3, 1],
                        [4, 1],
                    ],
                    [
                        [0, 2],
                        [1, 2],
                        [2, 2],
                        [3, 2],
                        [4, 2],
                    ],
                    [
                        [0, 3],
                        [1, 3],
                        [2, 3],
                        [3, 3],
                        [4, 3],
                    ],
                ];
            case 7: // BOTTOM TO TOP
                return [
                    [
                        [0, 3],
                        [1, 3],
                        [2, 3],
                        [3, 3],
                        [4, 3],
                    ],
                    [
                        [0, 2],
                        [1, 2],
                        [2, 2],
                        [3, 2],
                        [4, 2],
                    ],
                    [
                        [0, 1],
                        [1, 1],
                        [2, 1],
                        [3, 1],
                        [4, 1],
                    ],
                    [
                        [0, 0],
                        [1, 0],
                        [2, 0],
                        [3, 0],
                        [4, 0],
                    ],
                ];
            case 8: // FROM CENTER
                return [
                    [
                        [2, 0],
                        [2, 1],
                        [2, 2],
                        [2, 3],
                    ],
                    [
                        [1, 0],
                        [1, 1],
                        [1, 2],
                        [1, 3],
                        [3, 0],
                        [3, 1],
                        [3, 2],
                        [3, 3],
                    ],
                    [
                        [0, 0],
                        [0, 1],
                        [0, 2],
                        [0, 3],
                        [4, 0],
                        [4, 1],
                        [4, 2],
                        [4, 3],
                    ],
                ];
            case 9: // TO CENTER
                return [
                    [
                        [0, 0],
                        [0, 1],
                        [0, 2],
                        [0, 3],
                        [4, 0],
                        [4, 1],
                        [4, 2],
                        [4, 3],
                    ],
                    [
                        [1, 0],
                        [1, 1],
                        [1, 2],
                        [1, 3],
                        [3, 0],
                        [3, 1],
                        [3, 2],
                        [3, 3],
                    ],
                    [
                        [2, 0],
                        [2, 1],
                        [2, 2],
                        [2, 3],
                    ],
                ];
        }
    }
}

export const WinAnimatorExtendedAttributes = {
    ACTIVE_ELEMENTS: "ActiveElements",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/